import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DeleteTrashRemove, Info } from '../../../assets/icons';
import { RoutePath } from '../../../routes';
import {
  useCreateChargerModelMutation,
  useFetchChargerModelByIdQuery,
  useFetchChargerModelDeploymentQuery,
  useLazyFetchChargerModelImageUploadUrlQuery,
  useUpdateChargerModelMutation,
} from '../../../services/chargerModels.api';
import {
  ChargerModel,
  ChargerModelDeviceConfig,
  ChargerModelUpdate,
  ElectricalCurrentType,
  PowerControlUnit,
} from '../../../stores/types/chargerModels.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  FormInput,
  Icon,
  Label,
  LabelType,
  Snackbar,
} from '../../_ui';
import { Card } from '../../_ui/Card.component';
import { InputType } from '../../_ui/Input.component';
import { AlertPosition, AlertType } from '../../_ui/snack-bar/Snack-Bar.component';
import { Tab } from '../../_ui/tabs/Tab.component';
import Tabs from '../../_ui/tabs/Tabs.component';

const LEVEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'DC_FAST', label: 'DC Fast' },
  { value: 'LEVEL_2', label: 'AC Level 2' },
  { value: 'LEVEL_1', label: 'AC Level 1' },
];

const CONNECTOR_TYPE_VALUES = ['CCS_1', 'CCS_2', 'TESLA', 'TYPE_1', 'TYPE_2', 'CHADEMO'];

const POWER_CONTROL_UNIT_PRESETS: PowerControlUnit[] = ['AMP', 'WATT'];

const ELECTRICAL_CURRENT_TYPE_OPTIONS: ElectricalCurrentType[] = ['AC', 'DC'];

const MAX_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024;

const createEmptyChargerModel = (): ChargerModel => ({
  id: '',
  vendor: '',
  model: '',
  level: '',
  maxWattage: 0,
  maxAmperage: 0,
  evses: [{ id: '1', connectors: ['1'] }],
  connectors: [
    { id: '1', type: 'TYPE_1', maxWattage: 0, physicalLocation: 'MIDDLE' },
    { id: '2', type: 'TYPE_1', maxWattage: 0, physicalLocation: 'MIDDLE' },
  ],
  frontendInfo: {},
  deviceConfigDefaultsOcpp1_6: [],
  supportZeroAmps: false,
  independentPortCircuits: false,
  powerControlUnit: '',
  powerManagementApprovedFirmwareVersions: [],
});

const PHYSICAL_LOCATIONS = ['LEFT', 'RIGHT', 'MIDDLE'];

/** Filled control style (Figma: grey1 field, 44px min height, 4px radius) */
const selectClass =
  'w-full min-h-[44px] px-3 py-2.5 rounded bg-grey1 text-sm text-black border-0 focus:outline-none focus:ring-1 focus:ring-black disabled:opacity-60';

type ChargerModelEditLocationState = { duplicateFromId?: string };

export const ChargerModelEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateFromId =
    (location.state as ChargerModelEditLocationState | null)?.duplicateFromId ?? undefined;
  const { chargerModelId } = useParams<{ chargerModelId: string }>();
  const id = chargerModelId ?? '';
  const isCreateMode = id === 'new';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { refetch: refetchDeploymentRows } = useFetchChargerModelDeploymentQuery();

  const [localImagePreview, setLocalImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const {
    data: model,
    isLoading,
    isError,
  } = useFetchChargerModelByIdQuery(id, {
    skip: !id || isCreateMode,
  });

  const {
    data: duplicateSource,
    isLoading: isDuplicateLoading,
    isError: isDuplicateError,
  } = useFetchChargerModelByIdQuery(duplicateFromId ?? '', {
    skip: !isCreateMode || !duplicateFromId,
  });
  const { data: deploymentRows = [] } = useFetchChargerModelDeploymentQuery(undefined, {
    skip: !isCreateMode,
  });
  const [updateChargerModel, updateResult] = useUpdateChargerModelMutation();
  const [createChargerModel, createResult] = useCreateChargerModelMutation();
  const [fetchImageUploadUrl] = useLazyFetchChargerModelImageUploadUrlQuery();

  const [draft, setDraft] = useState<ChargerModel | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    if (isCreateMode) {
      setLocalImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setImageUploading(false);
      if (duplicateFromId) {
        setDraft(null);
      } else {
        setDraft(createEmptyChargerModel());
      }
      return;
    }
    setDraft(null);
  }, [id, isCreateMode, duplicateFromId]);

  useEffect(() => {
    if (!isCreateMode && model) {
      setDraft(cloneDeep(model));
    }
  }, [isCreateMode, model]);

  useEffect(() => {
    if (!isCreateMode || !duplicateFromId || !duplicateSource) return;
    const copy = cloneDeep(duplicateSource);
    copy.id = '';
    setDraft(copy);
  }, [isCreateMode, duplicateFromId, duplicateSource]);

  const searchSuffix = typeof window !== 'undefined' ? window.location.search : '';

  const goBack = () => {
    navigate(`/${RoutePath.CHARGER_MODELS}${searchSuffix}`);
  };

  const manufacturerPresetOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of deploymentRows) {
      const v = row.manufacturer?.trim();
      if (v) set.add(v);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [deploymentRows]);

  const buildPayloadForApi = (base: ChargerModel): ChargerModelUpdate => {
    const withId =
      isCreateMode && !base.id?.trim()
        ? {
            ...base,
            id: crypto.randomUUID(),
          }
        : base;
    const imageUrlVal = withId.frontendInfo?.imageUrl?.trim() ?? '';
    const pmVersions = (withId.powerManagementApprovedFirmwareVersions ?? [])
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    const pcu = withId.powerControlUnit?.trim() ?? '';
    const electricalCurrentType = withId.electricalCurrentType;

    return {
      ...withId,
      modelName: base.model.trim(),
      evses: withId.connectors.map((c) => ({ id: c.id, connectors: [c.id] })),
      frontendInfo: { ...withId.frontendInfo, imageUrl: imageUrlVal || undefined },
      powerControlUnit: pcu || undefined,
      electricalCurrentType: electricalCurrentType || undefined,
      supportZeroAmps: withId.supportZeroAmps,
      independentPortCircuits: withId.independentPortCircuits,
      powerManagementApprovedFirmwareVersions: pmVersions,
    };
  };

  const handleSave = async () => {
    if (!draft) return;
    setIsUpdateLoading(true);
    try {
      const payload = buildPayloadForApi(draft);
      if (isCreateMode && 'model' in payload) {
        await createChargerModel(payload).unwrap();
        Snackbar.show({
          message: t('support_section.charger_models_create_success'),
          alertType: AlertType.SUCCESS,
          position: AlertPosition.BOTTOM_LEFT,
          duration: 4000,
        });
      } else {
        await updateChargerModel(payload as ChargerModelUpdate).unwrap();
        Snackbar.show({
          message: t('support_section.charger_models_save_success'),
          alertType: AlertType.SUCCESS,
          position: AlertPosition.BOTTOM_LEFT,
          duration: 4000,
        });
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await refetchDeploymentRows();
      goBack();
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      Snackbar.show({
        message:
          err?.data?.message ||
          (isCreateMode
            ? t('support_section.charger_models_create_error')
            : t('support_section.charger_models_save_error')),
        alertType: AlertType.ERROR,
        position: AlertPosition.BOTTOM_LEFT,
        duration: 5000,
      });
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const canSubmitCreate = useMemo(() => {
    if (!draft || !isCreateMode) return true;
    return (
      draft.vendor.trim() !== '' &&
      draft.model.trim() !== '' &&
      draft.level.trim() !== '' &&
      draft.maxWattage > 0 &&
      draft.maxAmperage > 0
    );
  }, [draft, isCreateMode]);

  const updateDraft = (patch: Partial<ChargerModel>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const setImageUrl = (url: string) => {
    setLocalImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        frontendInfo: { ...prev.frontendInfo, imageUrl: url },
      };
    });
  };

  const applyImageFile = async (file: File | null) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      Snackbar.show({
        message: t('support_section.charger_models_image_file_too_large'),
        alertType: AlertType.ERROR,
        position: AlertPosition.BOTTOM_LEFT,
        duration: 5000,
      });
      return;
    }
    setLocalImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setImageUploading(true);
    try {
      const contentType = file.type || 'image/png';
      const { uploadUrl, publicUrl } = await fetchImageUploadUrl({
        filename: file.name,
        contentType,
      }).unwrap();
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType },
      });
      if (!putRes.ok) {
        throw new Error('upload_put_failed');
      }
      setImageUrl(publicUrl);
    } catch {
      Snackbar.show({
        message: t('support_section.charger_models_image_upload_error'),
        alertType: AlertType.ERROR,
        position: AlertPosition.BOTTOM_LEFT,
        duration: 5000,
      });
      setLocalImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    } finally {
      setImageUploading(false);
    }
  };

  const onDropImage = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (imageUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void applyImageFile(file);
  };

  const onImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) void applyImageFile(file);
    e.target.value = '';
  };

  const updateConnector = (
    index: number,
    patch: Partial<NonNullable<ChargerModel['connectors']>[number]>,
  ) => {
    if (!draft) return;
    const connectors = [...draft.connectors];
    connectors[index] = { ...connectors[index], ...patch };
    updateDraft({ connectors });
  };

  const removeConnector = (index: number) => {
    if (!draft) return;
    const connectors = draft.connectors.filter((_, i) => i !== index);
    updateDraft({ connectors });
  };

  const addConnector = () => {
    if (!draft) return;
    const nextId = String(Math.max(0, ...draft.connectors.map((c) => Number(c.id) || 0)) + 1);
    updateDraft({
      connectors: [
        ...draft.connectors,
        {
          id: nextId,
          type: 'TYPE_1',
          maxWattage: 0,
          physicalLocation: 'MIDDLE',
        },
      ],
    });
  };

  const updateDeviceConfig = (index: number, patch: Partial<ChargerModelDeviceConfig>) => {
    if (!draft?.deviceConfigDefaultsOcpp1_6) return;
    const list = [...draft.deviceConfigDefaultsOcpp1_6];
    list[index] = { ...list[index], ...patch };
    updateDraft({ deviceConfigDefaultsOcpp1_6: list });
  };

  const removeDeviceConfig = (index: number) => {
    if (!draft?.deviceConfigDefaultsOcpp1_6) return;
    updateDraft({
      deviceConfigDefaultsOcpp1_6: draft.deviceConfigDefaultsOcpp1_6.filter((_, i) => i !== index),
    });
  };

  const addDeviceConfig = () => {
    if (!draft) return;
    const list = draft.deviceConfigDefaultsOcpp1_6 ? [...draft.deviceConfigDefaultsOcpp1_6] : [];
    list.push({ key: 'ConnectionTimeOut', value: '', auditOnly: false });
    updateDraft({ deviceConfigDefaultsOcpp1_6: list });
  };

  const updatePmVersion = (index: number, value: string) => {
    if (!draft) return;
    const list = [...(draft.powerManagementApprovedFirmwareVersions ?? [])];
    list[index] = value;
    updateDraft({ powerManagementApprovedFirmwareVersions: list });
  };

  const removePmVersion = (index: number) => {
    if (!draft) return;
    updateDraft({
      powerManagementApprovedFirmwareVersions: (
        draft.powerManagementApprovedFirmwareVersions ?? []
      ).filter((_, i) => i !== index),
    });
  };

  const addPmVersion = () => {
    if (!draft) return;
    updateDraft({
      powerManagementApprovedFirmwareVersions: [
        ...(draft.powerManagementApprovedFirmwareVersions ?? []),
        '',
      ],
    });
  };

  if (!id) {
    return (
      <div>
        <Label
          type={LabelType.BODY2}
          text={t('support_section.charger_models_edit_invalid')}
          color={ColorType.NEGATIVE}
        />
      </div>
    );
  }

  if (!isCreateMode && isLoading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <Label text={t('loading')} type={LabelType.BODY2} color={ColorType.GREY5} />
      </div>
    );
  }

  if (isCreateMode && duplicateFromId && !isDuplicateLoading && isDuplicateError) {
    return (
      <div>
        <Label
          type={LabelType.BODY2}
          text={t('support_section.charger_models_load_error')}
          color={ColorType.NEGATIVE}
        />
        <div className='mt-4'>
          <Button label={t('back')} type={ButtonType.TERTIARY} onClick={goBack} />
        </div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className='flex items-center justify-center py-20'>
        <Label text={t('loading')} type={LabelType.BODY2} color={ColorType.GREY5} />
      </div>
    );
  }

  if (!isCreateMode && (isError || !model)) {
    return (
      <div>
        <Label
          type={LabelType.BODY2}
          text={t('support_section.charger_models_load_error')}
          color={ColorType.NEGATIVE}
        />
        <div className='mt-4'>
          <Button label={t('back')} type={ButtonType.TERTIARY} onClick={goBack} />
        </div>
      </div>
    );
  }

  const imageUrl = draft.frontendInfo?.imageUrl ?? '';
  const displayImageSrc = localImagePreview || imageUrl;

  return (
    <div className='w-full bg-web_bg min-h-full'>
      <Card className='max-w-[1152px] mx-auto rounded-lg'>
        <div className='flex flex-col gap-5 w-full'>
          <div className='flex items-center py-4 border-b border-grey2 -mx-5 px-5 -mt-5'>
            <Label
              text={
                isCreateMode
                  ? duplicateFromId
                    ? t('support_section.charger_models_duplicate_heading')
                    : t('support_section.charger_models_add_heading')
                  : t('support_section.charger_models_edit_heading')
              }
              type={LabelType.H4}
              color={ColorType.BLACK}
            />
          </div>

          <Tabs
            rootClass='max-w-[420px] border-b border-grey2'
            onTabChange={(i: number) => setActiveTabIndex(i)}
            tabClass='flex-1'
          >
            <Tab label={t('support_section.charger_models_tab_details')} dataTestId='tab-details' />
            <Tab label={t('support_section.charger_models_tab_json')} dataTestId='tab-json' />
          </Tabs>

          {activeTabIndex === 1 ? (
            <pre className='text-xs text-grey6 whitespace-pre-wrap font-mono bg-grey0 p-4 rounded-lg border border-grey2 overflow-auto max-h-[70vh]'>
              {JSON.stringify(draft, null, 2)}
            </pre>
          ) : (
            <>
              <div className='flex gap-2 items-start w-full'>
                <Icon
                  src={Info}
                  size={{ width: 20, height: 20 }}
                  className='shrink-0 mt-0.5'
                  alt=''
                />
                <Label
                  text={t('support_section.charger_models_image_hint')}
                  type={LabelType.BODY3}
                  color={ColorType.GREY6}
                  className='flex-1 whitespace-pre-wrap leading-5'
                />
              </div>

              <div className='flex flex-col gap-5 w-full'>
                <div className='flex flex-col lg:flex-row gap-6 items-start w-full'>
                  <div className='flex flex-col gap-3 w-full lg:w-[260px] shrink-0'>
                    <Label
                      text={t('support_section.charger_models_section_details')}
                      type={LabelType.LABEL_M}
                      color={ColorType.BLACK}
                    />
                    <div
                      className={`relative w-[260px] max-w-full aspect-square rounded-lg bg-grey0 overflow-hidden flex items-center justify-center border-2 border-dashed border-grey3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-black ${
                        imageUploading ? 'cursor-wait' : 'cursor-pointer'
                      }`}
                      role='button'
                      tabIndex={0}
                      aria-busy={imageUploading}
                      onClick={() => {
                        if (!imageUploading) fileInputRef.current?.click();
                      }}
                      onKeyDown={(e) => {
                        if (imageUploading) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={onDropImage}
                    >
                      <input
                        ref={fileInputRef}
                        type='file'
                        accept='image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp'
                        className='sr-only'
                        disabled={imageUploading}
                        onChange={onImageInputChange}
                      />
                      {displayImageSrc ? (
                        <img
                          src={displayImageSrc}
                          alt=''
                          className='w-full h-full object-contain pointer-events-none'
                        />
                      ) : (
                        <div className='flex flex-col items-center justify-center gap-2 px-4 text-center'>
                          <Label
                            type={LabelType.BODY3}
                            color={ColorType.GREY6}
                            text={t('support_section.charger_models_image_drop_title')}
                          />
                          <Label
                            type={LabelType.BODY4}
                            color={ColorType.GREY5}
                            text={t('support_section.charger_models_image_drop_formats')}
                          />
                        </div>
                      )}
                      {imageUploading && (
                        <div className='absolute inset-0 flex items-center justify-center bg-white/75'>
                          <Label
                            type={LabelType.BODY3}
                            color={ColorType.GREY6}
                            text={t('support_section.charger_models_image_uploading')}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='flex-1 flex flex-col gap-4 min-w-0 w-full lg:pt-9'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      {isCreateMode ? (
                        <div className='flex flex-col gap-2'>
                          <Label
                            text={t('support_section.charger_models_field_manufacturer')}
                            type={LabelType.LABEL_M}
                            color={ColorType.BLACK}
                          />
                          <input
                            type='text'
                            className={selectClass}
                            value={draft.vendor}
                            onChange={(e) => updateDraft({ vendor: e.target.value })}
                            placeholder={t(
                              'support_section.charger_models_placeholder_manufacturer',
                            )}
                            list='charger-model-manufacturer-presets'
                            autoComplete='off'
                          />
                          <datalist id='charger-model-manufacturer-presets'>
                            {manufacturerPresetOptions.map((v) => (
                              <option key={v} value={v} />
                            ))}
                          </datalist>
                        </div>
                      ) : (
                        <FormInput
                          label={t('support_section.charger_models_field_manufacturer')}
                          defaultValue={draft.vendor}
                          onChange={(e) => updateDraft({ vendor: e.target.value })}
                          disabled
                          width='100%'
                        />
                      )}
                      <FormInput
                        label={t('support_section.charger_models_field_model')}
                        defaultValue={draft.model}
                        onChange={(e) => updateDraft({ model: e.target.value })}
                        placeholder={
                          isCreateMode ? t('support_section.charger_models_placeholder_model') : ''
                        }
                        width='100%'
                      />
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div className='flex flex-col gap-2 md:col-span-2'>
                        <Label
                          text={t('support_section.charger_models_field_level')}
                          type={LabelType.LABEL_M}
                          color={ColorType.BLACK}
                        />
                        <select
                          className={selectClass}
                          value={draft.level}
                          disabled={!isCreateMode}
                          onChange={(e) => updateDraft({ level: e.target.value })}
                        >
                          {isCreateMode && (
                            <option value=''>
                              {t('support_section.charger_models_level_placeholder')}
                            </option>
                          )}
                          {LEVEL_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <FormInput
                        label={t('support_section.charger_models_field_max_wattage')}
                        inputType={InputType.FLOAT_NUMBER}
                        defaultValue={
                          isCreateMode && draft.maxWattage === 0 ? '' : String(draft.maxWattage)
                        }
                        onChange={(e) => updateDraft({ maxWattage: Number(e.target.value) || 0 })}
                        placeholder={
                          isCreateMode
                            ? t('support_section.charger_models_placeholder_max_wattage')
                            : ''
                        }
                        width='100%'
                      />
                      <FormInput
                        label={t('support_section.charger_models_field_max_amperage')}
                        inputType={InputType.FLOAT_NUMBER}
                        defaultValue={
                          isCreateMode && draft.maxAmperage === 0 ? '' : String(draft.maxAmperage)
                        }
                        onChange={(e) => updateDraft({ maxAmperage: Number(e.target.value) || 0 })}
                        placeholder={
                          isCreateMode
                            ? t('support_section.charger_models_placeholder_max_amperage')
                            : ''
                        }
                        width='100%'
                      />
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-2'>
                      <div className='flex flex-col gap-2'>
                        <Label
                          text={t('support_section.charger_models_field_power_control_unit')}
                          type={LabelType.LABEL_M}
                          color={ColorType.BLACK}
                        />
                        <input
                          type='text'
                          className={selectClass}
                          value={draft.powerControlUnit ?? ''}
                          onChange={(e) => updateDraft({ powerControlUnit: e.target.value })}
                          placeholder={t(
                            'support_section.charger_models_placeholder_power_control_unit',
                          )}
                          list='charger-model-pcu-presets'
                          autoComplete='off'
                        />
                        <datalist id='charger-model-pcu-presets'>
                          {POWER_CONTROL_UNIT_PRESETS.map((v) => (
                            <option key={v} value={v} />
                          ))}
                        </datalist>
                      </div>
                      <div className='flex flex-col gap-2'>
                        <Label
                          text={t('support_section.charger_models_field_electrical_current_type')}
                          type={LabelType.LABEL_M}
                          color={ColorType.BLACK}
                          labelHtmlFor='charger-model-electrical-current-type'
                          htmlFor='charger-model-electrical-current-type'
                        />
                        <select
                          id='charger-model-electrical-current-type'
                          className={selectClass}
                          value={draft.electricalCurrentType ?? ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateDraft({
                              electricalCurrentType: value
                                ? (value as ElectricalCurrentType)
                                : undefined,
                            });
                          }}
                        >
                          <option value=''>
                            {t(
                              'support_section.charger_models_placeholder_electrical_current_type',
                            )}
                          </option>
                          {ELECTRICAL_CURRENT_TYPE_OPTIONS.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className='flex flex-col gap-2'>
                        <Label
                          text={t('support_section.charger_models_field_independent_port_circuits')}
                          type={LabelType.LABEL_M}
                          color={ColorType.BLACK}
                          labelHtmlFor='charger-model-independent-port-circuits'
                          htmlFor='charger-model-independent-port-circuits'
                        />
                        <div className='min-h-[44px] flex items-center'>
                          <input
                            id='charger-model-independent-port-circuits'
                            type='checkbox'
                            className='w-4 h-4 rounded-sm form-checkbox text-brand2 border-grey3'
                            checked={draft.independentPortCircuits ?? false}
                            onChange={(e) =>
                              updateDraft({ independentPortCircuits: e.target.checked })
                            }
                            aria-label={t(
                              'support_section.charger_models_field_independent_port_circuits',
                            )}
                          />
                        </div>
                      </div>
                      <div className='flex flex-col gap-2'>
                        <Label
                          text={t('support_section.charger_models_field_support_zero_amps')}
                          type={LabelType.LABEL_M}
                          color={ColorType.BLACK}
                          labelHtmlFor='charger-model-support-zero-amps'
                          htmlFor='charger-model-support-zero-amps'
                        />
                        <div className='min-h-[44px] flex items-center'>
                          <input
                            id='charger-model-support-zero-amps'
                            type='checkbox'
                            className='w-4 h-4 rounded-sm form-checkbox text-brand2 border-grey3'
                            checked={draft.supportZeroAmps ?? false}
                            onChange={(e) => updateDraft({ supportZeroAmps: e.target.checked })}
                            aria-label={t('support_section.charger_models_field_support_zero_amps')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='flex flex-col gap-2 w-full md:col-span-2'>
                      <Label
                        text={t('support_section.charger_models_section_pm_versions')}
                        type={LabelType.LABEL_M}
                        color={ColorType.BLACK}
                      />
                      <Label
                        text={t('support_section.charger_models_section_pm_versions_sub')}
                        type={LabelType.BODY3}
                        color={ColorType.GREY6}
                      />
                      <div className='border border-grey2 rounded-lg overflow-hidden'>
                        <div className='grid grid-cols-[1fr_auto] gap-2 px-3 py-2 bg-grey0 border-b border-grey2 text-sm font-semibold text-grey6'>
                          <span>{t('support_section.charger_models_col_pm_version')}</span>
                          <span className='w-8' />
                        </div>
                        {(draft.powerManagementApprovedFirmwareVersions ?? []).length === 0 ? (
                          <div className='px-3 py-3 text-sm text-grey6'>
                            {t('support_section.charger_models_pm_versions_empty')}
                          </div>
                        ) : (
                          (draft.powerManagementApprovedFirmwareVersions ?? []).map((v, index) => (
                            <div
                              key={`pm-ver-${index}`}
                              className='grid grid-cols-[1fr_auto] gap-2 px-3 py-2 items-center border-b border-grey2 last:border-b-0'
                            >
                              <input
                                className={selectClass}
                                value={v}
                                onChange={(e) => updatePmVersion(index, e.target.value)}
                                placeholder={t(
                                  'support_section.charger_models_placeholder_pm_version',
                                )}
                              />
                              <button
                                type='button'
                                className='p-2 text-grey6 hover:text-negative'
                                onClick={() => removePmVersion(index)}
                                aria-label={t('support_section.charger_models_remove_pm_version')}
                              >
                                <Icon src={DeleteTrashRemove} />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                      <Button
                        label={t('support_section.charger_models_add_pm_version')}
                        type={ButtonType.TERTIARY}
                        size={ButtonSize.SMALL}
                        className='self-start'
                        onClick={addPmVersion}
                      />
                    </div>
                  </div>
                </div>

                <div className='w-full min-w-0'>
                  <FormInput
                    label={t('support_section.charger_models_field_image_url')}
                    labelType={LabelType.LABEL_S}
                    labelTypeColor={ColorType.GREY6}
                    defaultValue={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder={t('support_section.charger_models_image_url_placeholder')}
                    showOutline={false}
                    width='100%'
                    height='44px'
                    wrapperClassName='w-full max-w-full'
                    inputClass='!w-full min-w-0 max-w-full box-border'
                  />
                </div>
              </div>

              <div className='border-t border-grey2' />

              <div>
                <div className='flex flex-col gap-2 mb-4'>
                  <Label
                    text={t('support_section.charger_models_section_connectors')}
                    type={LabelType.LABEL_M}
                    color={ColorType.BLACK}
                  />
                  <Label
                    text={t('support_section.charger_models_section_connectors_sub')}
                    type={LabelType.BODY3}
                    color={ColorType.GREY6}
                  />
                </div>
                <div className='border border-grey2 rounded-lg overflow-x-auto'>
                  <div className='grid grid-cols-[minmax(7rem,1fr)_minmax(5rem,1fr)_minmax(5rem,1fr)_minmax(5rem,1fr)_minmax(7rem,1fr)_auto] gap-2 px-3 py-2 bg-grey0 border-b border-grey2 text-sm font-semibold text-grey6 min-w-[44rem]'>
                    <span>{t('support_section.charger_models_col_type')}</span>
                    <span>{t('support_section.charger_models_col_max_wattage')}</span>
                    <span>{t('support_section.charger_models_col_min_amps')}</span>
                    <span>{t('support_section.charger_models_col_max_amps')}</span>
                    <span>{t('support_section.charger_models_col_physical')}</span>
                    <span className='w-8' />
                  </div>
                  {draft.connectors.map((c, index) => (
                    <div
                      key={`${c.id}-${index}`}
                      className='grid grid-cols-[minmax(7rem,1fr)_minmax(5rem,1fr)_minmax(5rem,1fr)_minmax(5rem,1fr)_minmax(7rem,1fr)_auto] gap-2 px-3 py-2 items-center border-b border-grey2 last:border-b-0 min-w-[44rem]'
                    >
                      <select
                        className={selectClass}
                        value={c.type}
                        onChange={(e) => updateConnector(index, { type: e.target.value })}
                      >
                        {CONNECTOR_TYPE_VALUES.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                      <input
                        className={selectClass}
                        type='text'
                        inputMode='numeric'
                        autoComplete='off'
                        value={c.maxWattage ? String(c.maxWattage) : ''}
                        onChange={(e) => {
                          updateConnector(index, { maxWattage: Number(e.target.value) || 0 });
                        }}
                      />
                      <input
                        className={selectClass}
                        type='text'
                        inputMode='numeric'
                        autoComplete='off'
                        value={c.minAmps != null ? String(c.minAmps) : ''}
                        onChange={(e) => {
                          const raw = e.target.value.trim();
                          updateConnector(index, {
                            minAmps: raw === '' ? undefined : Number(raw) || 0,
                          });
                        }}
                      />
                      <input
                        className={selectClass}
                        type='text'
                        inputMode='numeric'
                        autoComplete='off'
                        value={c.maxAmps != null ? String(c.maxAmps) : ''}
                        onChange={(e) => {
                          const raw = e.target.value.trim();
                          updateConnector(index, {
                            maxAmps: raw === '' ? undefined : Number(raw) || 0,
                          });
                        }}
                      />
                      <select
                        className={selectClass}
                        value={c.physicalLocation ?? 'MIDDLE'}
                        onChange={(e) =>
                          updateConnector(index, { physicalLocation: e.target.value })
                        }
                      >
                        {PHYSICAL_LOCATIONS.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                      <button
                        type='button'
                        className='p-2 text-grey6 hover:text-negative'
                        onClick={() => removeConnector(index)}
                        aria-label={t('support_section.charger_models_remove_connector')}
                      >
                        <Icon src={DeleteTrashRemove} />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  label={t('support_section.charger_models_add_connector')}
                  type={ButtonType.TERTIARY}
                  size={ButtonSize.SMALL}
                  className='mt-2'
                  onClick={addConnector}
                />
              </div>

              <div>
                <div className='flex flex-col gap-2 mb-4'>
                  <Label
                    text={t('support_section.charger_models_section_ocpp')}
                    type={LabelType.LABEL_M}
                    color={ColorType.BLACK}
                  />
                  <Label
                    text={t('support_section.charger_models_section_ocpp_sub')}
                    type={LabelType.BODY3}
                    color={ColorType.GREY6}
                  />
                </div>
                <div className='border border-grey2 rounded-lg overflow-hidden'>
                  <div className='grid grid-cols-[1fr_1fr_1fr_auto] gap-2 px-3 py-2 bg-grey0 border-b border-grey2 text-sm font-semibold text-grey6'>
                    <span>{t('support_section.charger_models_col_key')}</span>
                    <span>{t('support_section.charger_models_col_value')}</span>
                    <span>{t('support_section.charger_models_col_audit')}</span>
                    <span className='w-8' />
                  </div>
                  {(draft.deviceConfigDefaultsOcpp1_6 ?? []).map((row, index) => (
                    <div
                      key={`${row.key}-${index}`}
                      className='grid grid-cols-[1fr_1fr_1fr_auto] gap-2 px-3 py-2 items-center border-b border-grey2 last:border-b-0'
                    >
                      <input
                        className={selectClass}
                        value={row.key}
                        onChange={(e) => updateDeviceConfig(index, { key: e.target.value })}
                        autoComplete='off'
                      />
                      <input
                        className={selectClass}
                        value={row.value}
                        onChange={(e) => updateDeviceConfig(index, { value: e.target.value })}
                      />
                      <select
                        className={selectClass}
                        value={row.auditOnly ? 'true' : 'false'}
                        onChange={(e) =>
                          updateDeviceConfig(index, { auditOnly: e.target.value === 'true' })
                        }
                        aria-label={t('support_section.charger_models_col_audit')}
                      >
                        <option value='false'>false</option>
                        <option value='true'>true</option>
                      </select>
                      <button
                        type='button'
                        className='p-2 text-grey6 hover:text-negative'
                        onClick={() => removeDeviceConfig(index)}
                        aria-label={t('support_section.charger_models_remove_config')}
                      >
                        <Icon src={DeleteTrashRemove} />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  label={t('support_section.charger_models_add_config')}
                  type={ButtonType.TERTIARY}
                  size={ButtonSize.SMALL}
                  className='mt-2'
                  onClick={addDeviceConfig}
                />
              </div>
            </>
          )}

          {activeTabIndex === 0 && (
            <div className='flex justify-end gap-2 pt-4 border-t border-grey2'>
              <Button
                label={t('cancel')}
                type={ButtonType.TERTIARY}
                size={ButtonSize.SMALL}
                onClick={goBack}
                disabled={updateResult.isLoading || createResult.isLoading}
              />
              <Button
                label={
                  isCreateMode
                    ? t('support_section.charger_models_create')
                    : t('support_section.charger_models_save')
                }
                type={ButtonType.PRIMARY}
                size={ButtonSize.SMALL}
                onClick={handleSave}
                disabled={
                  isUpdateLoading ||
                  updateResult.isLoading ||
                  createResult.isLoading ||
                  (isCreateMode && !canSubmitCreate)
                }
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
